import { isValidMimeType, useUploadDocument } from "@api";
import { DocumentTypes } from "@bbj-types/document/enum";
import type { ObjectId } from "@bbj-types/utils";
import axios from "axios";
import { useState } from "react";
import type { IModalTrainingApplyForm } from "../types";

export interface IDocumentsActionsProgress {
  currentStep: number;
  totalSteps: number;
}

const PARTIAL_DOCUMENTS_ERROR_NAME = "DOCUMENTS_PARTIAL_UPLOAD_ERROR";

export const useModalTrainingApplyDocuments = () => {
  const [, uploadUrlDocument] = useUploadDocument();
  const [progress, setProgress] = useState<IDocumentsActionsProgress | null>(null);

  const handleTrainingApplyDocuments = async (documents: IModalTrainingApplyForm["documents"]): Promise<ObjectId[]> => {
    const files = documents.flatMap((document) => (document.file ? [document.file] : []));
    const createdDocuments: ObjectId[] = [];
    const failedDocuments: string[] = [];

    setProgress({ currentStep: 0, totalSteps: files.length });

    for (const file of files) {
      try {
        if (!isValidMimeType(file.type)) {
          failedDocuments.push(file.name);
        } else {
          const responseUploadUrl = await uploadUrlDocument(file.name, DocumentTypes.TRAINING_OFFER, file.type, true);
          await axios.postForm(responseUploadUrl.data.url, {
            ...responseUploadUrl.data.fields,
            file,
          });
          createdDocuments.push(responseUploadUrl.data.document._id);
        }
      } catch {
        failedDocuments.push(file.name);
      }

      setProgress((currentProgress) =>
        currentProgress
          ? {
              ...currentProgress,
              currentStep: currentProgress.currentStep + 1,
            }
          : currentProgress
      );
    }

    setProgress(null);

    if (failedDocuments.length > 0) {
      const error = new Error(
        `Impossible d'ajouter tous les documents (${failedDocuments.join(", ")}). Merci de corriger puis de réessayer.`
      );
      error.name = PARTIAL_DOCUMENTS_ERROR_NAME;
      throw error;
    }

    return createdDocuments;
  };

  return { documentsProgress: progress, handleTrainingApplyDocuments };
};
